import { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import UI Components
import { Paper } from '@geneui/components';
// Import Components
import CustomTabs from 'components/common/CustomTabs';
import DownloadOrSendToEmailToolbar from 'components/common/DownloadOrSendToEmailToolbar';
import ActivityKPI from 'components/common/ActivityKPI';
import PromotionTrackedLinksContent from 'components/common/PromotionTrackedLinksContent';
import CampaignActivityMetricsReceivedPlayers from './CampaignActivityMetricsReceivedPlayers';
import CampaignActivityMetricsPlayersWhoOpenedMail from './CampaignActivityMetricsPlayersWhoOpenedMail';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
// Import Services
import { Helpers } from 'services';
import { CampaignHttpService, CommunicationHttpService } from 'services/http';
// Import Constants
import {
    activityMetricsTabsKey as tabsKey,
    activityMetricsTabsKeyConfig as tabsKeyConfig,
    activityMetricsTabsActivityKPIs,
} from './config';
import { TemplateTypesLabels, l, CampaignType } from 'constants/common';
// Import Enums
import { FormatNumberActionEnum } from 'enums';

const { formatNumber } = Helpers;

const CampaignActivityMetrics = (props) => {
    const {
        campaignId,
        templateId,
        startDate,
        endDate,
        deliveryMethod,
        isLoadingSchedule,
        isActive: isActiveCampaignActivityMetrics,
    } = props;
    const { t } = useTranslation();
    const { showToaster } = useToaster();

    const { doGetRequest, doPostRequest } = useRequest();
    const [campaignKPIs, setCampaignKPIs] = useState({});
    const [isLoadingCampaignKPI, setIsLoadingCampaignKPI] = useState(false);
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [tabsDownloadDisabled, setTabsDownloadDisabled] = useState(() =>
        tabsKeyConfig.reduce((acc, val) => {
            acc[val] = true;
            return acc;
        }, {}),
    );

    const [tabActiveKey, setTabActiveKey] = useState(tabsKey.ReceivedPlayers);

    const isVisibleDownloadToolbar = [tabsKey.ReceivedPlayers, tabsKey.PlayersWhoOpenedMail].includes(tabActiveKey);

    const tabChangeHandler = (key) => {
        setTabActiveKey(key);
    };

    const { getPromotionGeneralReport } = useCancelableRequests({
        getPromotionGeneralReport: CommunicationHttpService.getPromotionGeneralReport,
    });

    // kept out of the hook: a file download must not be aborted when the user leaves the page
    const { downloadEmailClickReportRequest, downloadEmailReportRequest, downloadEmailOpenReportRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadEmailClickReportRequest: CampaignHttpService.downloadEmailClickReport(),
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadEmailReportRequest: CampaignHttpService.downloadEmailReport(),
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadEmailOpenReportRequest: CampaignHttpService.downloadEmailOpenReport(),
        }),
        [],
    );

    const getRate = (numerator, denominator) =>
        !denominator
            ? 0
            : formatNumber((numerator / denominator) * 100, {
                  type: FormatNumberActionEnum.CleanNumber,
                  minPrecision: 2,
                  maxPrecision: 5,
              });

    useEffect(() => {
        setIsLoadingCampaignKPI(true);
        doGetRequest(getPromotionGeneralReport.request, {
            queryString: {
                PromotionId: campaignId,
                CampaignType: CampaignType.AddHoc,
            },
            successCallback: ({
                Total,
                Sent,
                TotalOpened,
                TotalClicked,
                TotalUnsubscribed,
                TotalClickUnsubscribed,
            }) => {
                setCampaignKPIs({
                    total: Total,
                    sendCount: Sent,
                    openCount: TotalOpened,
                    clickCount: TotalClicked,
                    sendRate: getRate(Sent, Total),
                    openRate: getRate(TotalOpened, Sent),
                    clickRate: getRate(TotalClicked, TotalOpened),
                    unsubscribeCount: TotalUnsubscribed,
                    clickUnsubscribeCount: TotalClickUnsubscribed,
                });
            },
        }).then(() => {
            setIsLoadingCampaignKPI(false);
        });
    }, [campaignId, doGetRequest, getPromotionGeneralReport]);

    const disableDownloadSetters = useMemo(() => {
        const tabSetter = (tabKey) => (value) => {
            setTabsDownloadDisabled((prev) => ({ ...prev, [tabKey]: value }));
        };

        return {
            [tabsKey.ReceivedPlayers]: tabSetter(tabsKey.ReceivedPlayers),
            [tabsKey.PlayersWhoOpenedMail]: tabSetter(tabsKey.PlayersWhoOpenedMail),
            [tabsKey.PlayersWhoClickedTheLink]: tabSetter(tabsKey.PlayersWhoClickedTheLink),
        };
    }, []);

    const getContent = useCallback(
        (tabKey) => {
            const isActive = isActiveCampaignActivityMetrics && tabKey === tabActiveKey;

            switch (tabKey) {
                case tabsKey.ReceivedPlayers:
                    return (
                        <CampaignActivityMetricsReceivedPlayers
                            campaignId={campaignId}
                            startDate={startDate}
                            endDate={endDate}
                            setIsDisableDownload={disableDownloadSetters[tabKey]}
                            isLoadingSchedule={isLoadingSchedule}
                            isActive={isActive}
                        />
                    );
                case tabsKey.PlayersWhoOpenedMail:
                    return (
                        <CampaignActivityMetricsPlayersWhoOpenedMail
                            campaignId={campaignId}
                            startDate={startDate}
                            endDate={endDate}
                            setIsDisableDownload={disableDownloadSetters[tabKey]}
                            isLoadingSchedule={isLoadingSchedule}
                            isActive={isActive}
                        />
                    );
                case tabsKey.PlayersWhoClickedTheLink:
                    return (
                        <PromotionTrackedLinksContent
                            promotionId={campaignId}
                            templateId={templateId}
                            campaignType={CampaignType.AddHoc}
                            className={'activity-metrics-promotion-tracked-links-wrapper'}
                        />
                    );
                default:
                    return <></>;
            }
        },
        [
            isActiveCampaignActivityMetrics,
            tabActiveKey,
            campaignId,
            templateId,
            startDate,
            endDate,
            disableDownloadSetters,
            isLoadingSchedule,
        ],
    );

    const downloadAllOrSendEmailClickHandler = (isDownload) => {
        // eslint-disable-next-line default-case
        switch (tabActiveKey) {
            case tabsKey.ReceivedPlayers:
                return downloadAllOrSendEmail(downloadEmailReportRequest, isDownload);
            case tabsKey.PlayersWhoOpenedMail:
                return downloadAllOrSendEmail(downloadEmailOpenReportRequest, isDownload);
            case tabsKey.PlayersWhoClickedTheLink:
                return downloadAllOrSendEmail(downloadEmailClickReportRequest, isDownload);
        }
    };

    const downloadAllOrSendEmail = (request, isDownload) => {
        const requestBody = {
            CampaignId: campaignId,
            StartDate: startDate,
            EndDate: endDate,
            ReportType: 0,
        };
        setIsFileLoading(true);

        return doPostRequest(request.request, {
            requestBody,
            queryString: { isDownload },
            isShowError: false,
            successCallback: (data) => {
                isDownload && window.open(data?.Url);
            },
        }).then(({ AlertType, AlertMessage, IsCanceled }) => {
            // Aborted on unmount: no toaster
            if (IsCanceled) return;
            showToaster(AlertType, AlertMessage);
            setIsFileLoading(false);
        });
    };

    const tabs = useMemo(() => {
        return tabsKeyConfig
            .filter((el) => campaignKPIs[activityMetricsTabsActivityKPIs[el]] > 0 || el === tabsKey.ReceivedPlayers)
            .map((item) => {
                const label = isNil(deliveryMethod) ? t(l.Mail) : `${t(`${TemplateTypesLabels[deliveryMethod]}Label`)}`;
                return {
                    tabKey: item,
                    tabTitle: (
                        <p>
                            {t(l[item], {
                                deliveryMethod: label,
                            })}
                        </p>
                    ),
                    tabContent: getContent(item),
                };
            });
    }, [campaignKPIs, deliveryMethod, getContent, t]);

    return (
        <div className="activity-metrics-holder">
            <div className="activity-metrics-holder-inner">
                <ActivityKPI isLoading={isLoadingCampaignKPI} data={campaignKPIs} withHeader={false} />
                <Paper className="activity-metrics-section">
                    <CustomTabs activeTabKey={tabActiveKey} tabsConfig={tabs} tabChangeHandler={tabChangeHandler} />
                    {isVisibleDownloadToolbar && (
                        <ul className="module-header cr-position-radius p-top s-big">
                            <li className="right-content">
                                <DownloadOrSendToEmailToolbar
                                    downloadAction={() => downloadAllOrSendEmailClickHandler(true)}
                                    sendToEmailAction={() => downloadAllOrSendEmailClickHandler(false)}
                                    isFileLoading={isFileLoading}
                                    isDisabledDownload={tabsDownloadDisabled[tabActiveKey]}
                                />
                            </li>
                        </ul>
                    )}
                </Paper>
            </div>
        </div>
    );
};

CampaignActivityMetrics.propTypes = {
    campaignId: PropTypes.string.isRequired,
    templateId: PropTypes.number,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    isLoadingSchedule: PropTypes.bool.isRequired,
    isActive: PropTypes.bool,
    deliveryMethod: PropTypes.number,
};

CampaignActivityMetrics.defaultProps = {
    isActive: true,
};

export default memo(CampaignActivityMetrics);
